import React, { useState, useEffect, useMemo, useRef } from 'react';
import { emailApiService, EmailAccount, EmailTemplate } from '../../services/email/emailApiService';
import { getActiveRetailers, Retailer } from '../../services/retailerService';
import { getRetailerGroups, RetailerGroup } from '../../services/retailerGroupService';
import { getTenderWithSites, Tender, TenderSite } from '../../services/tenderService';
import { X, Paperclip, Send, Loader2, Users, CheckSquare, Square, FolderOpen, Search, ChevronDown, Check, Eye } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../foundation/altzero_base/base/components/ui/popover';
import { Badge } from '../../../../foundation/altzero_base/base/components/ui/badge';
import { useDropzone } from 'react-dropzone';
import { replacePlaceholders } from '../../utils/templateUtils';

const parseEmailSendError = (err: any): string => {
    const apiError = err?.response?.data?.error || err?.response?.data?.message || '';
    if (apiError.toLowerCase().includes('insufficient permission')) {
        return 'Your email account does not have the required permissions. Please reconnect your email account and make sure to grant all requested scopes.';
    }
    return err?.message || 'Failed to send email';
};

interface EmailCompositionDialogProps {
    isOpen: boolean;
    onClose: () => void;
    tenderId: string;
    defaultRecipients?: string[];
    defaultSubject?: string;
    defaultBody?: string;
    retailerId?: string; // If sending to specific retailer
    sites?: any[]; // Array of sites for context
    tenderName?: string; // Name of the tender
    tenderNotes?: string; // Notes from tender details
}

export const EmailCompositionDialog: React.FC<EmailCompositionDialogProps> = ({
    isOpen,
    onClose,
    tenderId,
    defaultRecipients = [],
    defaultSubject = '',
    defaultBody = '',
    retailerId,
    sites = [],
    tenderName = '',
    tenderNotes = '',
}) => {
    const [accounts, setAccounts] = useState<EmailAccount[]>([]);
    const [selectedAccount, setSelectedAccount] = useState<string>('');
    const [to, setTo] = useState<string>(defaultRecipients.join(', '));
    const [cc, setCc] = useState<string>('');
    const [subject, setSubject] = useState<string>(defaultSubject);
    const [body, setBody] = useState<string>(defaultBody);
    const [iframeReloadKey, setIframeReloadKey] = useState(0);
    const editableIframeRef = useRef<HTMLIFrameElement>(null);
    const [attachments, setAttachments] = useState<File[]>([]);
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // DB-fetched tender data (fresh on every open)
    const [dbTender, setDbTender] = useState<Tender | null>(null);
    const [dbSites, setDbSites] = useState<TenderSite[]>([]);

    // Template State
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [userSignature, setUserSignature] = useState<string>('');

    // Retailer State
    const [retailers, setRetailers] = useState<Retailer[]>([]);
    const [selectedRetailerId, setSelectedRetailerId] = useState<string>(retailerId || '');
    const [isBulkSend, setIsBulkSend] = useState(false);
    const [bulkSelectedRetailers, setBulkSelectedRetailers] = useState<string[]>([]);
    const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number } | null>(null);
    
    // Retailer Groups State
    const [retailerGroups, setRetailerGroups] = useState<RetailerGroup[]>([]);
    const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
    const [groupSearchQuery, setGroupSearchQuery] = useState('');
    const [retailerSearchQuery, setRetailerSearchQuery] = useState('');
    const [isGroupDropdownOpen, setIsGroupDropdownOpen] = useState(false);

    useEffect(() => {
        if (isOpen && tenderId) {
            loadInitialData();
        }
    }, [isOpen, tenderId]);

    // Notes are shown in Tender Context panel only, not injected into email body

    const loadInitialData = async () => {
        try {
            setLoading(true);
            const [accountsData, retailersResult, templatesData, groupsResult, tenderResult] = await Promise.all([
                emailApiService.getAccounts(),
                getActiveRetailers(),
                emailApiService.getTemplates('retailer_rfi'),
                getRetailerGroups(),
                getTenderWithSites(tenderId),
            ]);

            // Store fresh DB tender + sites
            console.log('[EmailDialog] tenderResult:', tenderResult);
            if (tenderResult.success && tenderResult.tender) {
                setDbTender(tenderResult.tender);
                setDbSites(tenderResult.sites || []);
                const wfSite0 = tenderResult.tender.workflow_data?.step1?.sites?.[0] ?? tenderResult.tender.workflow_data?.sites?.[0];
                console.log('[EmailDialog] workflow_data site[0]:', {
                    abn: wfSite0?.abn,
                    dmaProvider: wfSite0?.dmaProvider,
                    retailerComms: wfSite0?.retailerComms,
                    currentContractExpiry: wfSite0?.currentContractExpiry,
                    dueAt: wfSite0?.dueAt,
                    annualKwhPeak: wfSite0?.annualKwhPeak,
                    annualKwhOffpeak: wfSite0?.annualKwhOffpeak,
                    tender_sites_count: (tenderResult.sites || []).length,
                });
            } else {
                console.warn('[EmailDialog] DB fetch failed or tender missing:', tenderResult.error);
            }

            setAccounts(accountsData);
            // Pick the default/first active account and grab its signature
            const defaultAcc = accountsData.find(a => a.is_default && a.is_active) || accountsData.find(a => a.is_active);
            if (defaultAcc) {
                setSelectedAccount(defaultAcc.id);
            }
            const fetchedSignature = defaultAcc?.email_signature || '';
            setUserSignature(fetchedSignature);

            if (retailersResult.success && retailersResult.data) {
                setRetailers(retailersResult.data);
                if (retailerId) {
                    setSelectedRetailerId(retailerId);
                }
            }

            if (groupsResult.success && groupsResult.data) {
                setRetailerGroups(groupsResult.data);
            }

            setTemplates(templatesData);

            const freshRetailers = retailersResult.success ? (retailersResult.data || []) : [];
            const freshTender = tenderResult.success ? (tenderResult.tender ?? null) : null;
            const freshSites  = tenderResult.success ? (tenderResult.sites  ?? [])  : [];

            if (!defaultBody) {
                // No pre-filled body — apply the default template (which also appends signature)
                const defaultTemplate = templatesData.find(t => t.is_default);
                console.log('[EmailDialog] templates loaded:', templatesData.length, '| default template:', defaultTemplate?.name || 'NONE');
                if (defaultTemplate) {
                    setSelectedTemplateId(defaultTemplate.id);
                    // IMPORTANT: pass freshly-fetched data directly — React state hasn't committed yet
                    applyTemplateWithRetailers(defaultTemplate, retailerId, false, freshRetailers, fetchedSignature, freshTender, freshSites);
                }
            } else {
                // defaultBody was provided — append the account signature to it
                setBody(appendSignatureToBody(defaultBody, fetchedSignature));
                setIframeReloadKey(k => k + 1);
            }

        } catch (err: any) {
            setError('Failed to load initial data');
        } finally {
            setLoading(false);
        }
    };

    const buildPlaceholderData = (
        retailerName: string,
        freshTender?: Tender | null,
        freshSites?: TenderSite[]
    ) => {
        // Use freshly-fetched DB data (passed during initial load) or committed state
        const t = freshTender ?? dbTender;
        const s = freshSites ?? dbSites;

        // workflow_data.step1.sites contains the live camelCase data saved by the app
        // This is the primary source — direct tender columns are often null
        // Fall back to prop `sites` (in-memory context data) when DB hasn't been written yet
        const wfSites: any[] = t?.workflow_data?.step1?.sites || t?.workflow_data?.sites || sites || [];
        const wf = wfSites[0]; // first site from workflow_data

        // Map sites for templateUtils (use workflow_data sites when available,
        // fall back to tender_site DB snapshot)
        const mappedSites = wfSites.length > 0
            ? wfSites.map((site: any) => ({
                id: site.id || site.siteId || '',
                name: site.name || '',
                nmi: site.nmi || '',
                abn: site.abn || '',
                acn: site.acn || '',
                address: {
                    street:   site.address?.street   || '',
                    city:     site.address?.city     || '',
                    state:    site.address?.state    || '',
                    postcode: site.address?.postcode || '',
                },
                annualKwhPeak:     site.annualKwhPeak     ?? null,
                annualKwhOffpeak:  site.annualKwhOffpeak  ?? null,
                annualKwhShoulder: site.annualKwhShoulder ?? null,
                retailerNotes:     site.retailerNotes     || '',
                retailerComms:     site.retailerComms     || '',
                dueAt:             site.dueAt             || null,
                // New contract starts the day after current contract expires
                startDate: (() => {
                    const expiry = site.currentContractExpiry || site.startDate;
                    if (!expiry) return null;
                    const d = new Date(expiry);
                    if (isNaN(d.getTime())) return expiry;
                    d.setDate(d.getDate() + 1);
                    return d.toISOString();
                })(),
            }))
            : (s || []).map(site => ({
                id: site.id,
                name: site.name || '',
                nmi: site.nmi || '',
                abn: (site as any).abn || '',
                acn: (site as any).acn || '',
                address: {
                    street:   site.addr_street   || '',
                    city:     site.addr_city     || '',
                    state:    site.addr_state    || '',
                    postcode: site.addr_postcode || '',
                },
                annualKwhPeak:     site.annual_kwh_peak     ?? null,
                annualKwhOffpeak:  site.annual_kwh_offpeak  ?? null,
                annualKwhShoulder: site.annual_kwh_shoulder ?? null,
                retailerNotes:     (site as any).retailer_notes  || '',
                retailerComms:     (site as any).retailer_comms || '',
                dueAt:             (site as any).due_at          || null,
                startDate: (() => {
                    const expiry = (site as any).current_contract_expiry;
                    if (!expiry) return null;
                    const d = new Date(expiry);
                    if (isNaN(d.getTime())) return expiry;
                    d.setDate(d.getDate() + 1);
                    return d.toISOString();
                })(),
            }));

        const firstSite = mappedSites[0];
        const rawFirstSite = (s || [])[0]; // DB tender_site snapshot fallback

        const siteAddr = firstSite?.address
            ? [firstSite.address.street, firstSite.address.city,
               firstSite.address.state, firstSite.address.postcode].filter(Boolean).join(', ')
            : '';
        const tenderAddr = t
            ? [t.addr_street, t.addr_city, t.addr_state, t.addr_postcode].filter(Boolean).join(', ')
            : '';

        // Priority: workflow_data site (camelCase) → tender direct column → tender_site snapshot
        const pick = (...vals: (string | null | undefined)[]) =>
            vals.find(v => v != null && v !== '') || '';

        return {
            retailerName,
            tenderName: t?.opportunity_name || t?.workflow_data?.step1?.tenderName || tenderName || 'Tender',
            contactName: 'Team',
            organizationName: 'Choice Energy',
            sites: mappedSites,
            tenderNotes: t?.workflow_data?.step1?.sites?.[0]?.retailerNotes || t?.notes || tenderNotes || '',
            // Fields: workflow_data.sites[0] first, then direct tender columns, then site snapshot
            entityName:     pick(t?.entity_name, t?.workflow_data?.step1?.clientData?.name),
            abn:            pick((t?.client as any)?.abn, t?.workflow_data?.step1?.clientData?.abn, t?.abn),
            acn:            pick(t?.workflow_data?.step1?.clientData?.acn, t?.acn),
            startDate:      (() => {
                const expiry = pick(wf?.currentContractExpiry, t?.current_contract_expiry, rawFirstSite?.current_contract_expiry);
                if (!expiry) return '';
                const d = new Date(expiry);
                if (isNaN(d.getTime())) return expiry;
                d.setDate(d.getDate() + 1);
                return d.toISOString();
            })(),
            pricingDueDate: pick(wf?.dueAt instanceof Date ? wf.dueAt.toISOString() : wf?.dueAt, t?.due_at),
            comms:          '', // Comms moved to per-site column in Site Details Table
            dma:            pick(wf?.dmaProvider,             t?.dma_provider,            rawFirstSite?.dma_provider),
            address:        siteAddr || tenderAddr,
            contractTerms:  '12, 24, 36 & 48 Month',
            specialRequests: pick(wf?.specialRequests, t?.special_requests, rawFirstSite?.special_requests),
            solarCapacity:       '',
            estimatedGeneration: '',
            estimatedFeedIn:     '',
        };
    };

    // Markers so we can reliably swap signature when account changes
    const SIG_MARKER = '<!--email-sig-->';

    const appendSignatureToBody = (html: string, sig: string): string => {
        if (!sig?.trim()) return html;
        return (
            html +
            `\n${SIG_MARKER}<div style="margin-top:16px;font-size:0.78em;line-height:1.4;color:#555;"><hr style="border:none;border-top:1px solid #e2e8f0;margin-bottom:10px;"/>${sig}</div>`
        );
    };

    const stripSignatureFromBody = (html: string): string => {
        const idx = html.indexOf(SIG_MARKER);
        return idx !== -1 ? html.slice(0, idx) : html;
    };

    // Core replacement logic — accepts explicit retailer list + signature + fresh DB data to avoid stale state reads
    const applyTemplateWithRetailers = (
        template: EmailTemplate,
        rId: string | undefined,
        bulk: boolean,
        retailerList: Retailer[],
        sig: string = userSignature,
        freshTender?: Tender | null,
        freshSites?: TenderSite[]
    ) => {
        if (bulk) {
            // Fill all placeholders with real data EXCEPT {{Retailer Name}} — keep it for per-send personalization
            const data = buildPlaceholderData('', freshTender, freshSites);
            setSubject(replacePlaceholders(template.subject, data));
            const bulkBodyHtml = replacePlaceholders(template.body_html, data);
            setBody(appendSignatureToBody(bulkBodyHtml, sig));
            setIframeReloadKey(k => k + 1);
        } else {
            const retailer = retailerList.find(r => r.id === rId)
                || retailerList.find(r => r.id === retailerId);
            const data = buildPlaceholderData(retailer?.name || '', freshTender, freshSites);
            console.log('[EmailDialog] buildPlaceholderData result:', { retailerName: data.retailerName, entityName: data.entityName, abn: data.abn, dma: data.dma, comms: data.comms, startDate: data.startDate, pricingDueDate: data.pricingDueDate, sites: data.sites?.length });
            setSubject(replacePlaceholders(template.subject, data));
            const bodyHtml = replacePlaceholders(template.body_html, data);
            setBody(appendSignatureToBody(bodyHtml, sig));
            setIframeReloadKey(k => k + 1);
        }
    };

    // Convenience wrapper that uses the committed `retailers` state (safe after initial load)
    const applyTemplate = (template: EmailTemplate, rId: string | undefined, bulk: boolean) => {
        applyTemplateWithRetailers(template, rId, bulk, retailers);
    };

    // When user switches the From Account, swap the signature in the current body
    const handleAccountChange = (accountId: string) => {
        setSelectedAccount(accountId);
        const newAccount = accounts.find(a => a.id === accountId);
        const newSig = newAccount?.email_signature || '';
        setUserSignature(newSig);
        setBody(prev => appendSignatureToBody(stripSignatureFromBody(prev), newSig));
        setIframeReloadKey(k => k + 1);
    };

    const handleTemplateChange = (templateId: string) => {
        setSelectedTemplateId(templateId);
        const template = templates.find(t => t.id === templateId);
        if (template) {
            applyTemplate(template, selectedRetailerId, isBulkSend);
        }
    };

    const handleRetailerChange = (rId: string) => {
        setSelectedRetailerId(rId);
        const retailer = retailers.find(r => r.id === rId);

        if (retailer && retailer.contact_email) {
            setTo(retailer.contact_email);
        } else {
            setTo('');
        }

        // If a template is selected, re-apply it for the new retailer
        if (selectedTemplateId && !isBulkSend) {
            const template = templates.find(t => t.id === selectedTemplateId);
            if (template) {
                applyTemplate(template, rId, false);
            }
        }
    };

    const handleBulkModeToggle = (checked: boolean) => {
        setIsBulkSend(checked);
        // Clear selections when switching modes
        if (!checked) {
            setBulkSelectedRetailers([]);
            setSelectedGroupIds([]);
            setGroupSearchQuery('');
            setRetailerSearchQuery('');
        }
        // When switching modes, re-apply template if selected
        if (selectedTemplateId) {
            const template = templates.find(t => t.id === selectedTemplateId);
            if (template) {
                applyTemplate(template, selectedRetailerId, checked);
            }
        }
    };

    const onDrop = (acceptedFiles: File[]) => {
        // Validate PDF files only
        const invalidFiles = acceptedFiles.filter(file => {
            const extension = file.name.split('.').pop()?.toLowerCase();
            const mimeType = file.type.toLowerCase();
            return extension !== 'pdf' && mimeType !== 'application/pdf';
        });

        if (invalidFiles.length > 0) {
            setError(`Only PDF files are allowed. Please remove: ${invalidFiles.map(f => f.name).join(', ')}`);
            return;
        }

        setAttachments(prev => [...prev, ...acceptedFiles]);
        setError(null);
    };

    const { getRootProps, getInputProps } = useDropzone({ 
        onDrop,
        accept: {
            'application/pdf': ['.pdf']
        }
    });

    const removeAttachment = (index: number) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const toggleBulkSelection = (rId: string) => {
        setBulkSelectedRetailers(prev => {
            const isSelected = prev.includes(rId);
            
            if (isSelected) {
                return prev.filter(id => id !== rId);
            } else {
                // Adding a retailer manually
                return [...prev, rId];
            }
        });
    };

    const toggleSelectAll = () => {
        // Get retailers already selected via groups
        const groupRetailerIds = new Set<string>();
        selectedGroupIds.forEach(groupId => {
            const group = retailerGroups.find(g => g.id === groupId);
            if (group?.retailer_ids) {
                group.retailer_ids.forEach(id => groupRetailerIds.add(id));
            }
        });

        // Check if all non-group retailers are selected
        const nonGroupRetailers = filteredRetailersForBulk.map(r => r.id);
        const allNonGroupSelected = nonGroupRetailers.length > 0 && 
            nonGroupRetailers.every(id => bulkSelectedRetailers.includes(id));

        if (allNonGroupSelected) {
            // Deselect all non-group retailers
            setBulkSelectedRetailers(Array.from(groupRetailerIds));
        } else {
            // Select all non-group retailers (keep group selections)
            const allRetailerIds = new Set([...bulkSelectedRetailers, ...nonGroupRetailers]);
            setBulkSelectedRetailers(Array.from(allRetailerIds));
        }
    };

    // Handle group selection
    const handleGroupToggle = (groupId: string) => {
        setSelectedGroupIds(prev => {
            const isSelected = prev.includes(groupId);
            const newGroupIds = isSelected 
                ? prev.filter(id => id !== groupId)
                : [...prev, groupId];
            
            // Update bulk selected retailers based on groups
            updateRetailersFromGroups(newGroupIds, groupId, !isSelected);
            
            return newGroupIds;
        });
    };

    // Update retailers list when groups are selected/deselected
    const updateRetailersFromGroups = (groupIds: string[], changedGroupId: string, isAdding: boolean) => {
        const changedGroup = retailerGroups.find(g => g.id === changedGroupId);
        if (!changedGroup) return;

        // Use active retailers array (filtered at service layer) rather than raw retailer_ids
        const activeIds = changedGroup.retailers?.map(r => r.id) ?? [];

        if (isAdding) {
            // Adding a group: add only active retailers
            setBulkSelectedRetailers(prev => {
                const newIds = new Set(prev);
                activeIds.forEach(id => newIds.add(id));
                return Array.from(newIds);
            });
        } else {
            // Removing a group: remove its retailers (unless they're in other selected groups)
            setBulkSelectedRetailers(prev => {
                const retailersInOtherGroups = new Set<string>();
                groupIds.forEach(gId => {
                    const group = retailerGroups.find(g => g.id === gId);
                    group?.retailers?.forEach(r => retailersInOtherGroups.add(r.id));
                });

                return prev.filter(id => {
                    // Keep if it's in another selected group, or if it's not in the removed group
                    return retailersInOtherGroups.has(id) || !activeIds.includes(id);
                });
            });
        }
    };

    // Filter groups and retailers based on search
    const filteredGroups = useMemo(() => {
        if (!groupSearchQuery.trim()) return retailerGroups;
        const query = groupSearchQuery.toLowerCase();
        return retailerGroups.filter(group =>
            group.name.toLowerCase().includes(query) ||
            group.retailers?.some(r => r.name.toLowerCase().includes(query))
        );
    }, [retailerGroups, groupSearchQuery]);

    const filteredRetailersForBulk = useMemo(() => {
        let filtered = retailers;
        
        if (retailerSearchQuery.trim()) {
            const query = retailerSearchQuery.toLowerCase();
            filtered = retailers.filter(r =>
                r.name.toLowerCase().includes(query) ||
                r.contact_email?.toLowerCase().includes(query)
            );
        }

        // Show retailers that are not already selected via groups
        const groupRetailerIds = new Set<string>();
        selectedGroupIds.forEach(groupId => {
            const group = retailerGroups.find(g => g.id === groupId);
            if (group?.retailer_ids) {
                group.retailer_ids.forEach(id => groupRetailerIds.add(id));
            }
        });

        return filtered.filter(r => !groupRetailerIds.has(r.id));
    }, [retailers, retailerSearchQuery, selectedGroupIds, retailerGroups]);

    // Read edited content from iframe (user may have changed text directly)
    const getBodyHtml = (): string => {
        const doc = editableIframeRef.current?.contentDocument;
        if (doc) return doc.documentElement.outerHTML;
        return body;
    };

    const handleSend = async () => {
        if (!selectedAccount) {
            setError('Please select an email account');
            return;
        }

        // Collect site document paths
        const siteDocumentPaths = sites.flatMap(s => s.documentFilePaths || []);

        try {
            setSending(true);
            setError(null);

            if (isBulkSend) {
                if (bulkSelectedRetailers.length === 0) {
                    setError('Please select at least one retailer for bulk sending');
                    setSending(false);
                    return;
                }

                setBulkProgress({ current: 0, total: 1 });

                // ── Option 2 bulk send ──────────────────────────────────────────
                // ONE email is sent. All retailers go in BCC (hidden from each other).
                // TO = notification address from the CC field, or the sender's own
                // account email as fallback. {{Retailer Name}} is left blank since
                // the body is common across all recipients.
                // ───────────────────────────────────────────────────────────────

                // Collect + deduplicate valid retailer emails
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                const bccEmails: string[] = [];
                const skippedRetailers: string[] = [];
                const seenEmails = new Set<string>();

                for (const rId of bulkSelectedRetailers) {
                    const retailer = retailers.find(r => r.id === rId);
                    if (!retailer || !retailer.contact_email) {
                        skippedRetailers.push(`${retailer?.name || rId}: no email`);
                        continue;
                    }
                    if (!emailRegex.test(retailer.contact_email)) {
                        skippedRetailers.push(`${retailer.name}: invalid email`);
                        continue;
                    }
                    const key = retailer.contact_email.toLowerCase();
                    if (seenEmails.has(key)) {
                        skippedRetailers.push(`${retailer.name}: duplicate`);
                        continue;
                    }
                    seenEmails.add(key);
                    bccEmails.push(retailer.contact_email);
                }

                if (bccEmails.length === 0) {
                    setError('No valid retailer emails to send to');
                    setSending(false);
                    return;
                }

                // TO: notification address from CC field, or sender's own account as fallback
                const senderEmail = accounts.find(a => a.id === selectedAccount)?.email || '';
                const toAddresses = cc.trim()
                    ? cc.split(',').map(e => e.trim()).filter(e => e)
                    : [senderEmail];

                // Build body with common template — {{Retailer Name}} left empty
                const bodyTemplate = getBodyHtml() || body;
                const bulkData    = buildPlaceholderData('');
                const bulkBody    = replacePlaceholders(bodyTemplate, bulkData);
                const bulkSubject = replacePlaceholders(subject, bulkData);

                try {
                    await emailApiService.sendEmail({
                        accountId: selectedAccount,
                        to: toAddresses,
                        bcc: bccEmails,
                        subject: bulkSubject,
                        bodyHtml: bulkBody,
                        tenderId,
                        attachments,
                        siteDocumentPaths: JSON.stringify(siteDocumentPaths)
                    });

                    setBulkProgress({ current: 1, total: 1 });

                    if (skippedRetailers.length > 0) {
                        setError(`Sent to ${bccEmails.length} retailers via BCC. Skipped: ${skippedRetailers.join('; ')}`);
                    } else {
                        onClose();
                    }
                } catch (e: any) {
                    setError(parseEmailSendError(e));
                }

            } else {
                // Single Send
                if (!selectedRetailerId && !to.trim()) {
                    setError('Please select a retailer or enter a recipient email address');
                    setSending(false);
                    return;
                }

                const recipients = to.split(',').map(e => e.trim()).filter(e => e);

                // Validate emails
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                const invalidEmails = recipients.filter(email => !emailRegex.test(email));

                if (invalidEmails.length > 0) {
                    setError(`Invalid email address(es): ${invalidEmails.join(', ')}`);
                    setSending(false);
                    return;
                }

                const isValidUUID = selectedRetailerId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(selectedRetailerId);

                await emailApiService.sendEmail({
                    accountId: selectedAccount,
                    to: recipients,
                    cc: cc.trim() ? cc.split(',').map(e => e.trim()).filter(e => e) : undefined,
                    subject,
                    bodyHtml: getBodyHtml(),
                    tenderId,
                    retailerId: isValidUUID ? selectedRetailerId : undefined,
                    attachments,
                    siteDocumentPaths: JSON.stringify(siteDocumentPaths)
                } as any);

                onClose();
            }

        } catch (err: any) {
            setError(parseEmailSendError(err));
        } finally {
            setSending(false);
            setBulkProgress(null);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-[98vw] max-h-[96vh] flex flex-col">
                <div className="flex justify-between items-center p-4 border-b">
                    <h3 className="text-xl font-semibold flex items-center gap-2">
                        <Send className="w-5 h-5 text-blue-600" />
                        Compose RFI Email
                    </h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6 flex-1 overflow-y-auto grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Left Column: Email Form (8 cols) */}
                    <div className="lg:col-span-8 space-y-5">
                        {error && (
                            <div className="bg-red-50 text-red-600 p-4 rounded-md border border-red-200 text-sm">
                                {error}
                            </div>
                        )}

                        {loading ? (
                            <div className="flex justify-center py-12">
                                <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
                            </div>
                        ) : accounts.length === 0 ? (
                            <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed">
                                <p className="text-gray-500 mb-4">No email accounts connected.</p>
                                <button
                                    onClick={() => window.open('/tender/email', '_blank')}
                                    className="text-blue-600 hover:underline font-medium"
                                >
                                    Connect an account in settings
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-5">
                                {/* Sender & Mode Selection */}
                                <div className="flex flex-col md:flex-row gap-4 p-4 bg-gray-50 rounded-lg border">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">From Account</label>
                                        <select
                                            value={selectedAccount}
                                            onChange={(e) => handleAccountChange(e.target.value)}
                                            className="w-full border rounded-md p-2 bg-white"
                                        >
                                            {accounts.map(acc => (
                                                <option key={acc.id} value={acc.id} disabled={!acc.is_active}>
                                                    {acc.email} {!acc.is_active && '(Inactive)'}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="flex items-end pb-2">
                                        <label className="flex items-center gap-2 cursor-pointer select-none">
                                            <input
                                                type="checkbox"
                                                checked={isBulkSend}
                                                onChange={(e) => handleBulkModeToggle(e.target.checked)}
                                                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                                            />
                                            <span className="font-medium text-gray-800">Bulk Send to Multiple Retailers</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Template Selection */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Template</label>
                                    <select
                                        value={selectedTemplateId}
                                        onChange={(e) => handleTemplateChange(e.target.value)}
                                        className="w-full border rounded-md p-2 bg-white"
                                    >
                                        <option value="">-- Select a Template --</option>
                                        {templates.map(t => (
                                            <option key={t.id} value={t.id}>
                                                {t.name} {t.is_default ? '(Default)' : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Recipient Selection */}
                                {isBulkSend ? (
                                    <div className="space-y-4">
                                        {/* Group Selection */}
                                        <div className="border rounded-md p-4 bg-gradient-to-br from-purple-50/50 to-blue-50/50 border-purple-100">
                                            <div className="flex items-center gap-2 mb-3">
                                                <FolderOpen className="w-4 h-4 text-purple-600" />
                                                <label className="text-sm font-medium text-gray-700">Select Retailer Groups</label>
                                            </div>
                                            {retailerGroups.length > 0 ? (
                                                <>
                                                    <Popover open={isGroupDropdownOpen} onOpenChange={setIsGroupDropdownOpen}>
                                                        <PopoverTrigger asChild>
                                                            <button
                                                                type="button"
                                                                className="w-full flex items-center justify-between px-3 py-2 text-sm border rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1"
                                                            >
                                                                <span className="text-gray-700">
                                                                    {selectedGroupIds.length > 0
                                                                        ? `${selectedGroupIds.length} group${selectedGroupIds.length !== 1 ? 's' : ''} selected`
                                                                        : 'Select groups...'}
                                                                </span>
                                                                <ChevronDown className="h-4 w-4 text-gray-400" />
                                                            </button>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="w-[400px] p-0" align="start">
                                                            <div className="p-3 border-b">
                                                                <div className="relative">
                                                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Search groups..."
                                                                        value={groupSearchQuery}
                                                                        onChange={(e) => setGroupSearchQuery(e.target.value)}
                                                                        className="w-full pl-10 pr-3 py-2 border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="max-h-[300px] overflow-y-auto p-2">
                                                                {filteredGroups.length > 0 ? (
                                                                    <div className="space-y-1">
                                                                        {filteredGroups.map(group => {
                                                                            const isSelected = selectedGroupIds.includes(group.id);
                                                                            return (
                                                                                <label
                                                                                    key={group.id}
                                                                                    className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${
                                                                                        isSelected 
                                                                                            ? 'bg-purple-50 hover:bg-purple-100' 
                                                                                            : 'hover:bg-gray-50'
                                                                                    }`}
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                >
                                                                                    <div className="flex items-center justify-center w-5 h-5 border-2 rounded border-gray-300 flex-shrink-0">
                                                                                        {isSelected && (
                                                                                            <Check className="h-4 w-4 text-purple-600" />
                                                                                        )}
                                                                                    </div>
                                                                                    <div className="flex-1 min-w-0">
                                                                                        <div className="text-sm font-medium text-gray-900 truncate">
                                                                                            {group.name}
                                                                                        </div>
                                                                                        <div className="text-xs text-gray-500">
                                                                                            {group.retailer_count || 0} retailer{(group.retailer_count || 0) !== 1 ? 's' : ''}
                                                                                        </div>
                                                                                    </div>
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={isSelected}
                                                                                        onChange={() => handleGroupToggle(group.id)}
                                                                                        className="sr-only"
                                                                                    />
                                                                                </label>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : (
                                                                    <div className="text-center py-8 text-gray-500 text-sm">
                                                                        No groups found matching "{groupSearchQuery}"
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </PopoverContent>
                                                    </Popover>

                                                    {/* Selected Groups Badges */}
                                                    {selectedGroupIds.length > 0 && (
                                                        <div className="mt-3 flex flex-wrap gap-2">
                                                            {selectedGroupIds.map(groupId => {
                                                                const group = retailerGroups.find(g => g.id === groupId);
                                                                if (!group) return null;
                                                                return (
                                                                    <Badge
                                                                        key={groupId}
                                                                        variant="secondary"
                                                                        className="bg-purple-100 text-purple-700 hover:bg-purple-200 px-3 py-1 flex items-center gap-2"
                                                                    >
                                                                        <FolderOpen className="w-3 h-3" />
                                                                        <span>{group.name}</span>
                                                                        <span className="text-xs opacity-75">
                                                                            ({group.retailer_count || 0})
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleGroupToggle(groupId)}
                                                                            className="ml-1 hover:bg-purple-300 rounded-full p-0.5 transition-colors"
                                                                            aria-label={`Remove ${group.name}`}
                                                                        >
                                                                            <X className="w-3 h-3" />
                                                                        </button>
                                                                    </Badge>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <p className="text-xs text-gray-500 italic py-2">
                                                    No retailer groups available. Create groups in Retailers Management.
                                                </p>
                                            )}
                                        </div>

                                        {/* Individual Retailer Selection */}
                                        <div className="border rounded-md p-4 bg-blue-50/50 border-blue-100">
                                            <div className="flex justify-between items-center mb-3">
                                                <label className="text-sm font-medium text-gray-700">Select Individual Retailers</label>
                                                <button
                                                    onClick={toggleSelectAll}
                                                    className="text-xs font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                                >
                                                    {filteredRetailersForBulk.length > 0 && bulkSelectedRetailers.length === filteredRetailersForBulk.length ? <CheckSquare size={14} /> : <Square size={14} />}
                                                    {filteredRetailersForBulk.length > 0 && bulkSelectedRetailers.length === filteredRetailersForBulk.length ? 'Deselect All' : 'Select All'}
                                                </button>
                                            </div>
                                            {filteredRetailersForBulk.length > 0 ? (
                                                <>
                                                    <div className="relative mb-3">
                                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                                                        <input
                                                            type="text"
                                                            placeholder="Search retailers..."
                                                            value={retailerSearchQuery}
                                                            onChange={(e) => setRetailerSearchQuery(e.target.value)}
                                                            className="w-full pl-10 pr-3 py-2 border rounded-md bg-white text-sm"
                                                        />
                                                    </div>
                                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[150px] overflow-y-auto pr-2">
                                                        {filteredRetailersForBulk.map(r => (
                                                            <label key={r.id} className="flex items-center gap-2 p-2 bg-white rounded border cursor-pointer hover:border-blue-300 transition-colors">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={bulkSelectedRetailers.includes(r.id)}
                                                                    onChange={() => toggleBulkSelection(r.id)}
                                                                    className="rounded text-blue-600"
                                                                />
                                                                <span className="text-sm truncate" title={r.name}>{r.name}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </>
                                            ) : (
                                                <p className="text-xs text-gray-500 italic py-2">
                                                    {retailerSearchQuery ? 'No retailers found matching your search.' : 'All retailers are already selected via groups.'}
                                                </p>
                                            )}
                                        </div>

                                        {/* Summary */}
                                        <div className="bg-blue-100/50 border border-blue-200 rounded-md p-3">
                                            <p className="text-sm font-medium text-blue-900">
                                                <Users className="w-4 h-4 inline mr-1" />
                                                {bulkSelectedRetailers.length} retailer{bulkSelectedRetailers.length !== 1 ? 's' : ''} selected
                                                {selectedGroupIds.length > 0 && (
                                                    <span className="text-blue-700 font-normal ml-2">
                                                        ({selectedGroupIds.length} group{selectedGroupIds.length !== 1 ? 's' : ''})
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-xs text-blue-700 mt-1">
                                                Individual personalized emails will be sent to each retailer.
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Select Retailer <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={selectedRetailerId}
                                                onChange={(e) => handleRetailerChange(e.target.value)}
                                                className="w-full border rounded-md p-2 bg-white"
                                            >
                                                <option value="">-- Custom / Manual --</option>
                                                {retailers.map(r => (
                                                    <option key={r.id} value={r.id}>{r.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                To <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={to}
                                                onChange={(e) => setTo(e.target.value)}
                                                placeholder="recipient@example.com"
                                                className="w-full border rounded-md p-2 bg-white"
                                                title="Enter recipient email address"
                                            />
                                            {selectedRetailerId && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Auto-populated from selected retailer
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {isBulkSend
                                            ? <>Notification Email <span className="text-gray-400 font-normal">(TO — optional)</span></>
                                            : <>CC <span className="text-gray-400 font-normal">(optional)</span></>
                                        }
                                    </label>
                                    <input
                                        type="text"
                                        value={cc}
                                        onChange={(e) => setCc(e.target.value)}
                                        placeholder="broker@example.com"
                                        className="w-full border rounded-md p-2 bg-white"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        {isBulkSend
                                            ? 'All retailers will be BCC\'d. This address goes in TO. Leave blank to use your own account.'
                                            : 'Separate multiple addresses with commas'
                                        }
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                                    <input
                                        type="text"
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        className="w-full border rounded-md p-2 font-medium"
                                    />
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="text-sm font-medium text-gray-700">
                                            Message Body {isBulkSend && <span className="text-blue-600 font-normal">(Template)</span>}
                                        </label>
                                        <span className="text-xs text-gray-400 flex items-center gap-1">
                                            <Eye className="h-3 w-3" /> Click any text to edit
                                        </span>
                                    </div>
                                    <iframe
                                        key={iframeReloadKey}
                                        ref={editableIframeRef}
                                        srcDoc={/<!DOCTYPE\s+html|<html\b/i.test(body) ? body : `<!DOCTYPE html><html><body style="font-family:'Segoe UI',Arial,sans-serif;color:#1a1a2e;padding:24px;margin:0;">${body}</body></html>`}
                                        title="Email editor"
                                        className="w-full border rounded-md bg-white"
                                        style={{ height: '480px' }}
                                        sandbox="allow-same-origin"
                                        onLoad={() => {
                                            const doc = editableIframeRef.current?.contentDocument;
                                            if (doc) doc.designMode = 'on';
                                        }}
                                    />
                                    {isBulkSend && (
                                        <p className="text-xs text-blue-600 mt-1">
                                            In bulk mode, {"{{Retailer Name}}"} will be replaced for each retailer automatically.
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <div {...getRootProps()} className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center cursor-pointer hover:bg-gray-50 hover:border-blue-400 transition-all">
                                        <input {...getInputProps()} />
                                        <Paperclip className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                                        <p className="text-sm font-medium text-gray-700">Drag & drop PDF files here, or click to select files</p>
                                        <p className="text-xs text-gray-500 mt-1">PDF files only</p>
                                    </div>

                                    {attachments.length > 0 && (
                                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            {attachments.map((file, index) => (
                                                <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm border group hover:border-blue-300 transition-colors">
                                                    <span className="truncate max-w-[80%] flex items-center gap-2">
                                                        <Paperclip className="w-3 h-3 text-gray-400" />
                                                        {file.name} <span className="text-gray-400 text-xs">({(file.size / 1024).toFixed(1)} KB)</span>
                                                    </span>
                                                    <button onClick={() => removeAttachment(index)} className="text-gray-400 hover:text-red-500 transition-colors">
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Column: Context & Attachments (4 cols) */}
                    <div className="lg:col-span-4 space-y-6">
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-100 shadow-sm">
                            <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                                <FileTextIcon /> Tender Context
                            </h4>
                            <div className="text-sm space-y-3 text-blue-800">
                                <div className="flex justify-between border-b border-blue-200 pb-2">
                                    <span className="font-medium text-blue-600">Tender Name</span>
                                    <span className="font-semibold">{tenderName}</span>
                                </div>
                                <div className="flex flex-col gap-1 pt-1">
                                    <span className="font-medium text-blue-600">Notes</span>
                                    <span className="text-blue-800 whitespace-pre-wrap leading-relaxed">
                                        {(dbTender?.workflow_data?.step1?.sites?.[0]?.retailerNotes)?.trim() || <span className="text-blue-400 italic">No notes</span>}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-5 rounded-xl border shadow-sm h-full max-h-[500px] flex flex-col">
                            <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                <Paperclip className="w-4 h-4 text-gray-500" />
                                Site Documents
                            </h4>
                            <p className="text-xs text-gray-500 mb-4">
                                These documents are linked to the sites and will be automatically attached to the email.
                            </p>

                            <div className="space-y-2 overflow-y-auto flex-1 pr-1">
                                {sites.flatMap(site =>
                                    (site.documentFilePaths || []).map((path: string) => ({
                                        siteName: site.name,
                                        fileName: path.split('/').pop() || path,
                                        path
                                    }))
                                ).length > 0 ? (
                                    sites.flatMap(site =>
                                        (site.documentFilePaths || []).map((path: string, idx: number) => (
                                            <div key={`${site.id}-${idx}`} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors">
                                                <div className="bg-white p-1.5 rounded border shadow-sm">
                                                    <Paperclip className="w-4 h-4 text-blue-500" />
                                                </div>
                                                <div className="overflow-hidden flex-1">
                                                    <p className="font-medium truncate text-gray-700 text-sm" title={path.split('/').pop()}>{path.split('/').pop()}</p>
                                                    <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                                                        {site.name}
                                                    </p>
                                                </div>
                                            </div>
                                        ))
                                    )
                                ) : (
                                    <div className="text-sm text-gray-400 italic p-6 text-center border-2 border-dashed rounded-lg bg-gray-50">
                                        No site documents found.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-4 border-t bg-gray-50 flex justify-between items-center rounded-b-lg">
                    <div className="text-sm text-gray-500">
                        {bulkProgress && (
                            <span className="flex items-center gap-2 text-blue-600 font-medium animate-pulse">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Sending {bulkProgress.current} of {bulkProgress.total}...
                            </span>
                        )}
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="px-5 py-2.5 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                            disabled={sending}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSend}
                            disabled={sending || loading || accounts.length === 0 || (isBulkSend && bulkSelectedRetailers.length === 0)}
                            className="flex items-center gap-2 bg-blue-600 text-white px-8 py-2.5 rounded-lg hover:bg-blue-700 transition-all disabled:opacity-50 shadow-md hover:shadow-lg font-medium"
                        >
                            {sending ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    {isBulkSend ? 'Sending Bulk Emails...' : 'Sending Email...'}
                                </>
                            ) : (
                                <>
                                    <Send className="w-5 h-5" />
                                    {isBulkSend ? `Send to ${bulkSelectedRetailers.length} Retailers` : 'Send RFI Email'}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const FileTextIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600">
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
);
